import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { Input } from "@/components/ui/input";
import { Check, Search, Barcode } from "lucide-react";
import { cn } from "@/lib/utils";
import { useBarcodeScanner } from "@/hooks/useBarcodeScanner";
import { useDebounce } from "@/hooks/useDebounce";
import { useToast } from "@/components/ui/use-toast";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

// Search configuration
const DEBOUNCE_DELAY = 300;
const MIN_SEARCH_LENGTH = 1;

const ProductSearch = ({ products, onSelect, className }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedTerm = useDebounce(searchTerm.trim().toLowerCase(), DEBOUNCE_DELAY);
  const [scannerEnabled, setScannerEnabled] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchInputRef = useRef(null);
  const { toast } = useToast();

  // Reset selection when search changes
  useEffect(() => {
    setSelectedIndex(-1);
  }, [debouncedTerm, products]);

  // Get business settings to check if negative stock is allowed
  const { business } = useSelector((state) => state.business);
  const allowNegativeStock = business?.allow_negative_stock || false;

  // Handle barcode scan
  const handleBarcodeScan = useCallback(async (barcode) => {
    setIsScanning(true);
    try {
      // Try to find product by barcode using dedicated endpoint
      const response = await axios.get(
        `${API_URL}products/barcode/${encodeURIComponent(barcode)}`,
        { withCredentials: true }
      );

      if (response.data.success && response.data.data) {
        const product = response.data.data;

        // Check if product is out of stock
        const isOutOfStock = product.stockQuantity <= 0;

        if (isOutOfStock && !allowNegativeStock) {
          // Block out-of-stock products only if negative stock is not allowed
          toast({
            title: "Out of Stock",
            description: `${product.title} is currently out of stock`,
            variant: "destructive"
          });
        } else {
          // Auto-select the product
          onSelect(product);
          setSearchTerm(""); // clear typed barcode from input
          toast({
            title: "Product Found",
            description: isOutOfStock
              ? `${product.title} added (Out of stock - will go negative)`
              : `${product.title} added to cart`,
            className: isOutOfStock
              ? "bg-orange-50 dark:bg-orange-900/30 border-orange-200"
              : "bg-green-50 dark:bg-green-900/30 border-green-200"
          });
        }
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast({
          title: "Product Not Found",
          description: `No product found with barcode: ${barcode}`,
          variant: "destructive"
        });
      } else {
        toast({
          title: "Error",
          description: "Failed to lookup product",
          variant: "destructive"
        });
      }
    } finally {
      setIsScanning(false);
      // Maintain continuous scanning by refocusing input
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 10);
    }
  }, [onSelect, toast]);

  // Enable barcode scanner
  useBarcodeScanner(handleBarcodeScan, {
    enabled: scannerEnabled,
    minLength: 3,
    timeout: 50
  });

  // Memoized search results with advanced filtering
  const filteredResults = useMemo(() => {
    // Only search if term meets minimum length
    if (debouncedTerm.length < MIN_SEARCH_LENGTH) return [];

    // Advanced multi-field search
    return (
      products
        ?.filter(
          (product) =>
            // Check if product is active (status 1)
            (product.status === 1) &&
            // Match product title
            (product.title?.toLowerCase().includes(debouncedTerm) ||
              // Match product ID
              product.id?.toString().includes(debouncedTerm) ||
              // Match barcode
              product.barcode?.toLowerCase().includes(debouncedTerm) ||
              // Match SKU (inside variations)
              (product.variations &&
                typeof product.variations === 'object' &&
                product.variations.sku?.toLowerCase().includes(debouncedTerm))
            )
        )
        .slice(0, 10) || []
    ); // Limit to 10 results
  }, [debouncedTerm, products]);

  // Handle product selection
  const handleSelect = useCallback(
    (product) => {
      onSelect(product);
      setSearchTerm("");
      setSelectedIndex(-1);
      
      // Keep input focused for rapid selection
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 10);
    },
    [onSelect]
  );

  const handleKeyDown = (e) => {
    if (filteredResults.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < filteredResults.length - 1 ? prev + 1 : prev));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev === 0 ? -1 : prev));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < filteredResults.length) {
        const product = filteredResults[selectedIndex];
        const isOutOfStock = (product.stockQuantity || 0) <= 0;
        const isDisabled = isOutOfStock && !allowNegativeStock;
        
        if (!isDisabled) {
           handleSelect(product);
        } else {
           toast({
             title: "Action Denied",
             description: `${product.title} is out of stock and cannot be added.`,
             variant: "destructive"
           });
        }
      } else if (filteredResults.length === 1) {
        // Auto-select if there's only one result and user hits enter without arrowing down
        const product = filteredResults[0];
        const isOutOfStock = (product.stockQuantity || 0) <= 0;
        if (!(isOutOfStock && !allowNegativeStock)) {
           handleSelect(product);
        }
      }
    }
  };

  return (
    <div className={cn("w-full relative", className)}>
      <div className="relative">
        <Input
          ref={searchInputRef}
          type="text"
          placeholder={isScanning ? "Scanning barcode..." : "Search by name, ID, or scan barcode..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          className={cn("w-full pl-10 pr-10", isScanning && "border-blue-500")}
          disabled={isScanning}
          autoFocus={true}
        />
        <Search
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
          size={20}
        />
        {scannerEnabled && (
          <Barcode
            className={cn(
              "absolute right-3 top-1/2 transform -translate-y-1/2",
              isScanning ? "text-blue-500 animate-pulse" : "text-green-500"
            )}
            size={20}
          />
        )}
      </div>

      {/* Search Results Dropdown */}
      {searchTerm.length >= MIN_SEARCH_LENGTH && (
        <div className="absolute z-50 w-full mt-1 bg-background border shadow-lg max-h-[300px] overflow-auto">
          {/* No Results State */}
          {filteredResults.length === 0 && (
            <div className="p-2 text-sm text-muted-foreground text-center">
              No products found
            </div>
          )}

          {/* Results List */}
          {filteredResults.map((product, index) => { // Use map block for logic
            const isOutOfStock = (product.stockQuantity || 0) <= 0;
            const isDisabled = isOutOfStock && !allowNegativeStock;
            const isSelected = index === selectedIndex;
            return (
              <div
                key={product.id}
                className={cn(
                  "flex items-center justify-between w-full px-3 py-2 transition-colors border-b last:border-0",
                  isDisabled
                    ? "opacity-50 cursor-not-allowed bg-gray-50 dark:bg-slate-800"
                    : "cursor-pointer hover:bg-accent",
                  isSelected && !isDisabled ? "bg-accent/80 bg-gray-100 dark:bg-slate-800 ring-2 ring-primary ring-inset" : "",
                  isOutOfStock && allowNegativeStock && "border-l-4 border-l-orange-400"
                )}
                onClick={() => {
                  if (!isDisabled) handleSelect(product);
                }}
                role="button"
                tabIndex={isDisabled ? -1 : 0}
              >
                <div className="flex items-center gap-3">
                  <Check className="w-4 h-4 opacity-0" />
                  <div>
                    <div className="font-medium flex items-center gap-2">
                      {product.title}
                      {isOutOfStock && (
                        <span className={cn(
                          "text-[10px] px-1.5 py-0.5 rounded-full font-bold",
                          allowNegativeStock
                            ? "bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400"
                            : "bg-red-100 text-red-600"
                        )}>
                          {allowNegativeStock ? "WILL GO NEGATIVE" : "OUT OF STOCK"}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      ID: {product.id} | Stock: <span className={isOutOfStock ? "text-red-500 font-bold" : "text-green-600"}>{product.stockQuantity || 0}</span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">GHS {parseFloat(product.current_price || 0).toFixed(2)}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProductSearch;
